import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Tooltip } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useApiInterceptor } from '@/providers/ApiLogsInterceptorProvider';

import { RequestLoggerTag } from './request-logger.common';

export const RequestLoggerHeader = () => {
  const {
    isApiLogsPaused,
    toggleApiLogsRealtimeStream,
    setSearchTerm,
    clearApiLogs,
    selectedApps,
    toggleApp,
    selectedTags,
    toggleTag,
    availableApps,
    availableTags,
    filteredLogs,
  } = useApiInterceptor();
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [apps, setApps] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [appFilter, setAppFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  useEffect(() => {
    setApps(availableApps);
  }, [availableApps]);

  useEffect(() => {
    setTags(availableTags);
  }, [availableTags]);

  useEffect(() => {
    if (apps.length || tags.length) return;
    setShowFilters((prev) => (prev ? false : prev));
  }, [apps.length, setShowFilters, tags.length]);
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchTerm(value);
      const debouncedSearch = debounce((searchValue: string) => {
        setSearchTerm(searchValue);
      }, 500);
      debouncedSearch(value);
    },
    [setSearchTerm]
  );

  const handleAppFilterChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAppFilter(e.target.value);
  };

  // Tag filter input change handler
  const handleTagFilterChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTagFilter(e.target.value);
  };

  // Clear app filter
  const clearAppFilter = () => {
    setAppFilter('');
  };

  // Clear tag filter
  const clearTagFilter = () => {
    setTagFilter('');
  };

  const filteredApps = apps.filter((app) =>
    appFilter ? app.toLowerCase().includes(appFilter.toLowerCase()) : true
  );

  const filteredTags = tags.filter((tag) =>
    tagFilter ? tag.toLowerCase().includes(tagFilter.toLowerCase()) : true
  );

  const hasActiveFilters = selectedApps.length > 0 || selectedTags.length > 0;
  const activeFiltersCount = selectedApps.length + selectedTags.length;

  //   const activeFilteredApps = useMemo(() => first, [second])

  const getActiveFiltersSummary = useMemo(() => {
    const parts = [];
    if (selectedApps.length > 0) {
      parts.push(`Apps: ${selectedApps.join(', ')}`);
    }
    if (selectedTags.length > 0) {
      parts.push(`Tags: ${selectedTags.join(', ')}`);
    }
    return parts.join('\n');
  }, [selectedApps, selectedTags]);

  return (
    <YStack className="flex justify-between gap-2">
      <XStack className="flex justify-between gap-2">
        <div className="relative w-full sm:max-w-sm">
          <Icons.search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground-subtle h-4 w-4" />
          <Input
            placeholder="Search requests..."
            value={localSearchTerm}
            onChange={handleSearch}
            disabled={filteredLogs.length === 0}
            className="pl-9 bg-surface border-border text-foreground"
          />
        </div>
        <XStack className="flex items-center gap-2">
          <Tooltip
            label={
              hasActiveFilters
                ? `Active filters: ${getActiveFiltersSummary}`
                : 'Filters'
            }
          >
            <Button
              disabled={!filteredLogs.length}
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={
                showFilters
                  ? 'bg-secondary text-foreground border-border'
                  : 'bg-secondary text-foreground-subtle border-border hover:bg-secondary-hover'
              }
              aria-label={`Filters ${hasActiveFilters ? `(${activeFiltersCount} active)` : ''}`}
            >
              <div className="relative">
                <Icons.filter
                  className={`h-4 w-4 mr-2 ${hasActiveFilters ? 'text-success-9' : ''}`}
                />
              </div>
              <span className={hasActiveFilters ? 'font-medium' : ''}>
                Filters
              </span>
            </Button>
          </Tooltip>
          <Button
            variant="default"
            onClick={toggleApiLogsRealtimeStream}
            className="flex items-center gap-2"
          >
            {isApiLogsPaused ? (
              <XStack className="flex items-center ">
                <Icons.play className="h-4 w-4 mr-2" />
                Play
              </XStack>
            ) : (
              <XStack className="flex items-center ">
                <Icons.pause className="h-4 w-4 mr-2" />
                Pause
              </XStack>
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={clearApiLogs}
            className="flex items-center gap-2"
          >
            Clear
          </Button>
        </XStack>
      </XStack>
      {showFilters && (
        <div className="border rounded-md p-4 space-y-4 bg-background-subtle border-border">
          {apps.length > 0 && (
            <div className="space-y-2">
              <YStack className="gap-2">
                <XStack className="items-center">
                  <Icons.filter className="h-4 w-4 mr-2 text-foreground-subtle" />
                  <span className="text-sm font-medium text-foreground">
                    Filter by app:
                  </span>
                </XStack>
                <div className="relative mb-2">
                  <Icons.search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground-subtle h-4 w-4" />
                  <Input
                    placeholder="Filter apps..."
                    value={appFilter}
                    onChange={handleAppFilterChange}
                    className="pl-9 text-sm bg-surface border-border text-foreground"
                    autoComplete="off"
                  />
                  {appFilter && (
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-foreground-subtle"
                      onClick={clearAppFilter}
                      type="button"
                    >
                      <Icons.cancel className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {filteredApps.map((app) => (
                    <RequestLoggerTag
                      key={app}
                      app={app}
                      selected={selectedApps.includes(app)}
                      onClick={() => toggleApp(app)}
                    />
                  ))}
                </div>
              </YStack>
            </div>
          )}

          {tags.length > 0 && (
            <div className="space-y-2">
              <YStack className="gap-2">
                <XStack className=" items-center">
                  <Icons.tag className="h-4 w-4 mr-2 text-foreground-subtle" />
                  <span className="text-sm font-medium text-foreground">
                    Filter by tag:
                  </span>
                </XStack>
                <div className="relative mb-2">
                  <Icons.search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground-subtle h-4 w-4" />
                  <Input
                    placeholder="Filter tags..."
                    value={tagFilter}
                    onChange={handleTagFilterChange}
                    className="pl-9 text-sm bg-surface border-border text-foreground"
                    autoComplete="off"
                  />
                  {tagFilter && (
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-foreground-subtle"
                      onClick={clearTagFilter}
                      type="button"
                    >
                      <Icons.cancel className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {filteredTags.map((tag) => (
                    <RequestLoggerTag
                      key={tag}
                      tag={tag}
                      selected={selectedTags.includes(tag)}
                      onClick={() => toggleTag(tag)}
                    />
                  ))}
                </div>
              </YStack>
            </div>
          )}
        </div>
      )}
    </YStack>
  );
};
